import {
  CareOrderStatus,
  CareOrderStatusEnum,
  type ColumnDef,
  getCareOrderStatusText,
  getOrderStatusText,
  getQuoteStatusText,
  isOfEnum,
  OrderStatus,
  OrdStatusEnum,
  QuoteStatus,
  QuoteStatusEnum,
  stringColumnComparator,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import {
  type CareOrderBlotterEntity,
  type CareOrderRow,
  isCareOrderRow,
  isLegRow,
  isOrderRow,
  isQuoteRow,
  type LegRow,
  type OrderRow,
  type QuoteRow,
} from '../types';
import { legRowFadedCellClassRules } from './legRowFadedCellClassRules';

interface StatusColumnParams {
  initialPinned: ColumnDef<CareOrderBlotterEntity>['initialPinned'];
}

/** Different construct at the two different levels */
type StatusColumnValue =
  | { type: 'child'; status: OrdStatusEnum | QuoteStatusEnum; statusText: string; data: QuoteRow | OrderRow | LegRow }
  | {
      type: 'parent';
      status: CareOrderStatusEnum;
      statusText: string;
      data: CareOrderRow;
      pendingFix: boolean;
    }
  | undefined;

/** Basic ValueGetter, just pulled out so it can be-reused */
function valueGetter(params: ValueGetterParams<CareOrderBlotterEntity>): StatusColumnValue {
  if (isCareOrderRow(params.data)) {
    const pendingFix = isFilledAndPendingFix(params.data);
    // This will run on CareOrder rows that don't have any children yet. For example, brand new care orders, or care orders with children, but they haven't been paginated to yet and added.
    return {
      type: 'parent',
      status: params.data.status,
      statusText: getCareOrderStatusText({
        status: params.data.status,
        pendingFix,
        formatMessage: params.context.current.intl.formatMessage,
      }),
      data: params.data,
      pendingFix,
    };
  }
  if (isOrderRow(params.data)) {
    return {
      type: 'child',
      status: params.data.OrdStatus,
      statusText: getOrderStatusText({ type: 'order', order: params.data, isCustomerOrder: false }),
      data: params.data,
    };
  }
  if (isQuoteRow(params.data) || isLegRow(params.data)) {
    return {
      type: 'child',
      status: params.data.QuoteStatus,
      statusText: getQuoteStatusText({ quoteStatus: params.data.QuoteStatus }),
      data: params.data,
    };
  }
  return undefined;
}

export const getCareOrderBlotterEntityStatusColumn: (
  params: StatusColumnParams
) => ColumnDef<CareOrderBlotterEntity> = column => ({
  type: 'custom',
  id: 'status',
  initialPinned: column.initialPinned,
  params: {
    initialWidth: 130,
    valueGetter,
    filterValueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>) => {
      // For filtering application, we just expose the status itself, and not the whole object used within the column
      return valueGetter(params)?.status;
    },
    cellRenderer: ({ value }: ICellRendererParams<CareOrderBlotterEntity, StatusColumnValue>) => {
      if (!value) {
        return null;
      }

      if (isCareOrderRow(value.data) && isOfEnum(value.status, CareOrderStatusEnum)) {
        return (
          <CareOrderStatus
            status={value.status}
            text={'data' in value ? value.data.Text : undefined}
            pendingFix={'pendingFix' in value ? value.pendingFix : undefined}
          />
        );
      }
      if (isOrderRow(value.data) && isOfEnum(value.status, OrdStatusEnum)) {
        return (
          <OrderStatus
            ordStatus={value.status}
            decisionStatus={value.data.DecisionStatus}
            cumQty={value.data.CumQty}
            orderQty={value.data.OrderQty}
            pricingMode={value.data.PricingMode}
            isCustomerOrder={false}
          />
        );
      }
      if (isQuoteRow(value.data) && isOfEnum(value.status, QuoteStatusEnum)) {
        return <QuoteStatus quoteStatus={value.status} />;
      }
      if (isLegRow(value.data) && isOfEnum(value.status, QuoteStatusEnum)) {
        return <QuoteStatus quoteStatus={value.status} />;
      }
      return null;
    },
    valueFormatter: ({ value }: ValueFormatterParams<unknown, StatusColumnValue>) => {
      if (!value) {
        return '';
      }

      // The statusText is the status "label" displayed to the user in the cell renderer. We display the same status in exports here,
      // and also use it for sorting comparisons below in the comparator
      return value.statusText;
    },
    comparator: (valueA: StatusColumnValue, valueB: StatusColumnValue) =>
      stringColumnComparator(valueA?.statusText, valueB?.statusText),
    cellClassRules: legRowFadedCellClassRules,
  },
});

function isFilledAndPendingFix(careOrderRow: CareOrderRow) {
  return careOrderRow.OrdStatus === OrdStatusEnum.Filled && careOrderRow.hasPendingFixingQty;
}
